export const featureFlagNames = [
  'ENABLE_BGP_ASPLAIN_FORMAT',
  'ENABLE_DEBUG_PATHLOOKUP',
  'ENABLE_DISCOVERY_DEVICES_ARUBANEWCENTRAL',
  'ENABLE_DISCOVERY_DEVICES_AVOCENT',
  'ENABLE_DISCOVERY_DEVICES_GCP',
  'ENABLE_DISCOVERY_DEVICES_NOKIA',
  'ENABLE_DISCOVERY_DEVICES_OPENGEAR_OM_CM_ACM',
  'ENABLE_DISCOVERY_DEVICES_VELOCLOUD',
  'ENABLE_DISCOVERY_DEVICES_ZPE',
  'ENABLE_FMC_NONEXPANDED_ICMP_CALL',
  'ENABLE_FMC_TOKEN_AUTH',
  'ENABLE_FMC_SERIAL_INTERFACE_DOWNLOAD',
  /**
   * Show hidden elements with composite hiding capability as semi-transparent, print hide reasons to console on click
   */
  'ENABLE_GRAPH_DUMPING_RENDERER_REQUEST_SOURCE',
  /**
   * Dump to console from where requestRender is being called in graphs; this can help to find which features
   * that affect the viewport are triggered and when.
   */
  'ENABLE_GRAPH_DEBUG_HIDDEN_NODE_REASONS',
  /**
   * Show dev tools for hook forms in the UI.
   *
   * Has to be integrated to every form where you want to use it.
   *
   * @example
   * const form = useCustomForm();
   * <form>
   *   {isFeatureFlagEnabled('ENABLE_HOOK_FORM_DEV_TOOLS') && (
   *     <DevTool control={form.control} placement='top-left' />
   *   )}
   * </form>
   */
  'ENABLE_HOOK_FORM_DEV_TOOLS',
  /**
   * Show an indicator in the GUI when a leave dialog is active on a page, and what is its state:
   * The indicator is shown on every page where leave dialog is used. Its state indicates whether
   * it is actually activated (e.g. there are unsaved changes) or not.
   *
   * This should not be visible for customers, but it is recommended to be turned on in all
   * development and internal testing environments, as issues in leave dialog state are hard to be noticed - there
   * is no way to see it is in wrong state until you don't try to leave the page, and even then
   * false negative means you have to realize that the leave dialog should have appeared.
   */
  'ENABLE_LEAVE_DIALOG_DEBUG_INDICATOR',
  /**
   * Enable new model for cloud devices in discovery (IPF-1669 and related tickets).
   * On by default since 8.2 (NIM-25701); `ENABLE_NEW_CLOUD_MODEL=false` falls back to the legacy model.
   */
  'ENABLE_NEW_CLOUD_MODEL',
  /**
   * Enable new model for cloud NAT & Internet access (IPF-2074)
   */
  'ENABLE_NCM_NAT_INTERNET',
  /**
   * Enable new model for cloud connectivity gateways (NIM-25465)
   */
  'ENABLE_NCM_CONNECTIVITY_GATEWAYS',
  'ENABLE_OPENGEAR_DOLLAR_PROMPT',
  'ENABLE_OPENGEAR_HASH_PROMPT',
  'ENABLE_SAVE_QUERY_LOG',
  'ENABLE_SAVE_SNAPSHOT_COLLECTIONS',
  'ENABLE_TEST_GENERATOR',
  'ENABLE_EXTENSIONS',
  'ENABLE_MANUAL_LINKS',
  'ENABLE_PALOALTO_EDL_IPLIST',
  'ENABLE_PALOALTO_EDL_URLLIST',
  'ENABLE_FAIL_ON_AQL_QUERY_WARNING',
  'ENABLE_EXPERIMENTAL_GIT_FILE_COMMIT',
  'ENABLE_EXPERIMENTAL_GIT_OPTIMIZE_REPOSITORY',
  'ENABLE_EXPERIMENTAL_GIT_CONFIGURE_REPOSITORY',
  'ENABLE_EXPERIMENTAL_SYSLOGWORKER_PERFORMANCE_LOGGING',
  'ENABLE_SHAPER_DISABLING',
  'ENABLE_JSON_SCHEMA_TASK_VALIDATION',
  'DISABLE_DISCOVERY_CLOUD_MANAGED_CATALYST_SWITCHES',
  /**
   * Enable Sentry integration. To actually send any data to Sentry, the DSN must also be provided in the
   * IPF_SENTRY_DSN environment variable. See '@ipfabric/backend-utils/sentry' for more details.
   */
  'ENABLE_SENTRY',
  /**
   * Enable Cisco line vty command (executed per vty range) to collect operational data from VTY lines.
   */
  'ENABLE_CISCO_LINE_VTY',
  /**
   * Use bulk-only commands for Cisco WLC AP and client discovery, both AireOS (wlc-air)
   * and IOS-XE (cat9800 / ct5760). Replaces per-AP/per-client CLI loops with a small set of
   * WLC-wide commands, reducing discovery time on large deployments at the cost of some field
   * granularity (e.g. no per-AP routing table, no per-client radio telemetry).
   * When enabled, the wlcApList and clientList tasks collect via bulk commands (fast path)
   * instead of the per-AP/per-client loops; the task identity and output shape are unchanged.
   * On by default since 8.2 (NIM-25869); `ENABLE_WLC_FAST_DISCOVERY=false` falls back to the per-AP/per-client loops.
   */
  'ENABLE_WLC_FAST_DISCOVERY',
  /**
   * Split the Versa VOS interfaces command into separate lightweight sub-commands
   * (brief, detail, dynamic-tunnels) instead of the heavy `interfaces?deep` call.
   * Improves performance on directors that struggle to return the full deep payload.
   */
  'ENABLE_VERSA_VOS_INTERFACES_SPLIT',
  /**
   * Labels, including label-based RBAC management (NIM-25575): endpoint policies,
   * data policies, label RBAC enrolment and the role form linking them.
   */
  'ENABLE_LABELS',
  /**
   * Mirror incoming discovery artifacts into the stage.artifacts staging table, in addition
   * to the regular data loading pipeline, to feed the upcoming ELT pipeline.
   */
  'ENABLE_ELT_PIPELINE',
  /**
   * Expose the AIM application detail page.
   */
  'ENABLE_AIM_APPLICATION_DETAIL',
  /**
   * Run the user-defined commands of Custom Discovery Data and store their parsed output
   * in the Custom Discovery Data table.
   */
  'ENABLE_CUSTOM_DISCOVERY_DATA',
] as const satisfies Readonly<
  (`ENABLE_${Uppercase<string>}` | `DISABLE_${Uppercase<string>}`)[]
>;
export type FeatureFlagName = (typeof featureFlagNames)[number];

export type FeatureFlags = Record<FeatureFlagName, boolean>;

export const defaultFeatureFlags: FeatureFlags = {
  ...featureFlagNames.reduce<FeatureFlags>((defaults, featureFlag) => {
    defaults[featureFlag] = false;
    return defaults;
  }, {} as FeatureFlags),
  // set `true` default values here
  ENABLE_NEW_CLOUD_MODEL: true,
  ENABLE_WLC_FAST_DISCOVERY: true,
};

export const isFeatureFlagEnabledByDefault = (
  featureFlag: FeatureFlagName,
): boolean => defaultFeatureFlags[featureFlag];
